import { CommonModule, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from 'primeng/api';
import { DatePickerModule } from 'primeng/datepicker';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';

import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { AlertStatusEnum } from '../../shared/enums/alert-status.enum';
import { AlertService } from '../../shared/services/alert/alert.service';
import { CommonService } from '../../shared/services/common/common.service';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import { SmsLogService } from '../../shared/services/sms-log/sms-log.service';
import { UtilitiesService } from '../../shared/services/utilities/utilities.service';
import { DateFormatterIso } from '../../shared/utilities/date-formatter.utils';
import { StringUtils } from '../../shared/utilities/string.utlis';
import { SmsModalComponent } from '../sms-modal/sms-modal.component';

@Component({
  selector: 'app-sms-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TableModule,
    IconField,
    InputIcon,
    Select,
    DatePickerModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    TooltipModule,
    SmsModalComponent,
    NgIf,
  ],
  templateUrl: './sms-log.component.html',
  styleUrl: './sms-log.component.scss',
})
export class SmsLogComponent implements OnInit {
  propertyId!: string;
  showSmsDrawer = false;
  allSmsLog: any[] = [];
  globalSearchValue = '';
  selectedSms: any = null;
  isDeleteModalOpen: boolean = false;
  alertId: string = '';

  selectedStatus: string | null = null;
  selectedDateRange: Date[] | null = null;
  isDateTouched = false;
  showDateRangeError = false;
  dateRangeErrorMessage = '';
  totalRecords = 0;
  loading = false;
  page = 1;
  limit = 10;
  private searchDebounceTimeout: any;

  DateFormatterIso = DateFormatterIso;
  AlertStatusEnum = AlertStatusEnum;

  statusList = Object.values(AlertStatusEnum).map((status) => ({
    label: StringUtils.snakeCaseToTitle(status),
    value: status,
  }));

  constructor(
    public readonly commonService: CommonService,
    private readonly localStorageService: LocalStorageService,
    private readonly smsLogService: SmsLogService,
    private readonly alertService: AlertService,
    private readonly utilitiesService: UtilitiesService,
  ) {}

  ngOnInit(): void {
    this.propertyId =
      this.localStorageService.getSessionPropertyStaff()?.property?.id;

    if (this.propertyId) {
      this.getAllSMSLog(this.propertyId);
    }
  }

  getAllSMSLog(propertyId: string): void {
    this.loading = true;
    const payload: any = {
      propertyId,
      page: this.page,
      limit: this.limit,
    };

    if (this.globalSearchValue?.trim()) {
      payload.search = this.globalSearchValue.trim();
    }

    if (this.selectedStatus) {
      payload.status = this.selectedStatus;
    }

    this.smsLogService.getAllSmsAlert(payload).subscribe({
      next: (res: any) => {
        const logs = res?.result?.data;
        this.totalRecords = res?.result?.total || 0;
        this.allSmsLog = logs.map((item: any) => ({
          id: item.id,
          date: item.createdAt,
          message: this.normalizeAlertMessage(item.message).body,
          sentVia: this.formatSentVia(item.sentVia),
          alertStatus: item.status,
          status: {
            total: item.total ?? 0,
            sent: item.sentCount ?? 0,
            failed: item.failedCount ?? 0,
          },
        }));
      },
      error: (error) => {
        console.error('Failed to load SMS logs', error);
        this.allSmsLog = [];
        this.totalRecords = 0;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  onPageChange(event: any): void {
    this.limit = event.rows;
    this.page = Math.floor(event.first / event.rows) + 1;
    this.getAllSMSLog(this.propertyId);
  }

  onStatusChange() {
    this.applyFilters();
  }
  applyFilters() {
    this.page = 1;
    this.getAllSMSLog(this.propertyId);
  }

  onGlobalSearchChange(event: Event) {
    const value = this.getTarget(event.target).value.trim();
    this.globalSearchValue = value;

    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.page = 1;
      this.getAllSMSLog(this.propertyId);
    }, 400);
  }

  getTarget(target: EventTarget | null): HTMLInputElement {
    return target as HTMLInputElement;
  }

  onDatePickerFocus() {
    this.isDateTouched = true;

    if (this.selectedDateRange && this.selectedDateRange.length < 2) {
      this.showDateRangeError = true;
    }
  }

  onDateRangeChange(value: Date[] | null) {
    if (!value) {
      return;
    }
    if (value.length === 2 && value[0] && !value[1]) {
      this.showDateRangeError = true;
      return;
    }
    this.showDateRangeError = false;
    this.isDateTouched = false;

    const [startDate, endDate] = value;
  }

  dateReset() {
    this.selectedDateRange = null;
    this.showDateRangeError = false;
  }

  clear(dt: any) {
    this.globalSearchValue = '';
    this.selectedStatus = null;
    this.selectedDateRange = null;
    this.isDateTouched = false;
    this.showDateRangeError = false;
    this.page = 1;
    this.getAllSMSLog(this.propertyId);
    dt.clear();
  }

  openViewModal(smsLog: any) {
    this.selectedSms = smsLog;
    this.showSmsDrawer = true;
  }

  /**
   * Same display rules as SMS modal Method column: `SMS` stays short;
   * otherwise `StringUtils.snakeCaseToTitle` (e.g. IN_APP_NOTIFICATION → In App Notification).
   */
  formatSentVia(sentVia: unknown): string {
    if (sentVia == null) return 'N/A';
    const parts = Array.isArray(sentVia)
      ? sentVia.map((x) => String(x).trim()).filter(Boolean)
      : [String(sentVia).trim()].filter(Boolean);
    if (parts.length === 0) return 'N/A';
    const labels = parts
      .map((p) => this.formatSentViaChannelLabel(p))
      .filter(Boolean);
    return labels.length ? labels.join(', ') : 'N/A';
  }

  private formatSentViaChannelLabel(method: string): string {
    if (!method) return '';
    return method === 'SMS'
      ? 'SMS'
      : StringUtils.snakeCaseToTitle(method);
  }

  private normalizeAlertMessage(raw: any): { body: string } {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed?.message) {
        return { body: parsed.message };
      }
      if (parsed?.body) {
        return { body: parsed.body };
      }
      return { body: String(parsed) };
    } catch {
      return { body: String(raw) };
    }
  }

  sendAgain(id: any) {
    // resend logic
  }

  openDeleteModal(id: string) {
    this.alertId = id;
    this.isDeleteModalOpen = true;
  }

  closeModal() {
    this.isDeleteModalOpen = false;
  }

  onDeleteSheet() {
    const smsLog = this.selectedSms;
    if (!smsLog?.id) {
      console.error('Sheet ID not found for deletion');
      return;
    } else {
    }
    // this.rulesService.deleteRulePricePolicy(rule.id).subscribe({
    //   next: (res: any) => {
    //   },
    //   error: (err: any) => {
    //     console.error('Error deleting rule:', err);
    //   },
    //   complete: () => {
    //     this.RULES = this.RULES.filter((r) => r.id !== rule.id);
    //     this.RULES = [...this.RULES];
    //     this.closeModal();
    //   },
    // });
  }

  // resendAlert(id: string): void {
  //   this.alertId = id;
  //   if (!this.alertId) {
  //     console.error('alertId is missing.');
  //     return;
  //   }

  //   this.alertService.resendAlert(this.alertId).subscribe({
  //     next: (res) => {
  //       this.showToast('Successful', 'Resend successfully');
  //     },
  //     error: (err) => {
  //       console.error('Send alert failed:', err);
  //     },
  //   });
  // }

  resendAlert(id: string): void {
    if (!id) {
      console.error('alertId is missing.');
      return;
    }

    const payload = {
      alertId: id,
    };

    this.alertService.resendAlert(payload).subscribe({
      next: () => {
        this.showToast('Successful', 'Resend successfully');
      },
      error: (err) => {
        console.error('Send alert failed:', err);
      },
    });
  }

  private showToast(title: string, message: string) {
    this.utilitiesService.showSwalWithToast(
      title,
      message,
      'success',
      true,
      5000,
    );
  }

  deleteBulkAlert() {
    const alertId = this.alertId;
    if (!alertId) {
      console.error('Cannot delete: id is missing!');
      return;
    }
    this.alertService.deleteBulkAlert(alertId).subscribe({
      next: (res) => {
        this.allSmsLog = this.allSmsLog.filter((item) => item.id !== alertId);
        this.closeModal();
      },
      error: (err) => {
        console.error('Failed to delete alert ', err);
      },
    });
  }
}
